import React, { useState } from 'react';
import Icon from '../Icons/Icon';
import { classNames, deviceIsMd, useNewWindow } from '../../Services/helpers';
import Modal from '../Modal';
import Section, { Sections } from '../Containers/Section';
import ButtonRow from '../Button/ButtonRow';
import Button from '../Button/Button';
import DateTime from '../Formatting/DateTime';
import TimeAgo from '../Formatting/TimeAgo';
import Alert from '../Alert';
import { activateDeferrerNotifications, cancelDelayedNotification } from '../../Data/Journal';
import { Grid, NoValue } from '@/Components/Primitives';

const MUTED = 'fill-muted';
const BRAND = 'fill-brand';
const GREEN = 'fill-green-600';
const RED = 'fill-red-500';

const ICON_NOT_SENT = {icon: 'Ban', className: MUTED, theme: 'solid'};
const ICON_SENT = {icon: 'Check', className: MUTED, theme: 'solid'};
const ICON_FAILED = {icon: 'TriangleExclamation', className: RED, theme: 'solid'};
const ICON_DELIVERED = {icon: 'DoubleCheck', className: MUTED, theme: 'solid'};
const ICON_READ = {icon: 'DoubleCheck', className: BRAND, theme: 'solid'};
const ICON_UNKNOWN = {icon: 'Question', className: MUTED, theme: 'solid'};

const NotificationLog = ({log, refreshThread}) => {
    const [isOpen, setIsOpen] = useState(false);
    const title = getNotificationLogTitle(log);

    if (!title) {
        return null;
    }

    return <>
        <div className={classNames(
                'w-[80%] rounded-xl py-2 px-3 mb-3 place-self-center ',
                'border border-orange-200 bg-orange-50/50 dark:bg-orange-900/50 dark:border-orange-700',
                'text-sm flex items-center justify-between cursor-pointer',
                (log.status === 'Deferred' || log.delayed) &&
                    'border-dotted border-orange-100 dark:border-orange-800 text-muted fill-muted w-[70%]'
            )}
            onClick={() => setIsOpen(true)}
        >
            <div className="grow flex flex-row items-center">
                <Icon icon="Bell" className="mr-2" />
                {title}
            </div>
            <div className="flex items-center justify-between gap-x-3">
                <RowContent log={log} />
            </div>
        </div>
        <Details {...{log, isOpen, setIsOpen, refreshThread}} />
    </>
}

const RowContent = ({log}) => {
    if (log.status === 'Deferred') {
        return <div className="flex flex-row items-center">
            <Icon icon="Timer" className="mr-2" />
            <span>Waiting</span>
        </div>
    }

    if (log.superseded) {
        return <div className="flex flex-row items-center">
            <Icon icon="SendBackward" className="mr-2" />
            <span className="text-muted">Superseded</span>
        </div>
    }

    if (log.delayed) {
        return <div className="flex flex-row items-center">
            <Icon icon="AlarmClock" className="mr-2" />
            <TimeAgo dateTime={log.notification?.delayedUntil} className="text-muted" />
        </div>
    }

    if (log.eventTriggerIgnored) {
        return <div className="flex flex-row items-center">
            <Icon icon="Pause" className="mr-2" />
            <span className="text-muted">Not sent</span>
        </div>
    }

    if (log.featureUnavailable) {
        return <div className="flex flex-row items-center">
            <Icon icon="Lock" className="mr-2" />
            <span>Unavailable</span>
        </div>
    }

    return <>
        <div>
            <Icon icon="PaperPlane" className="mr-1" />
            <Icon {...getEmailIcon(log)} />
        </div>
        <div>
            <Icon icon="Phone" className="mr-1" />
            <Icon {...getSMSIcon(log)} />
        </div>
    </>
}

const getNotificationLogTitle = (log) => {
    if (!log?.state?.notification) {
        return null;
    }

    const statesToTitle = {
        'ContactCustomerNotification': {
            'CustomerJobBecameToday': 'Job Due Today',
            'CustomerJobBecameTomorrow': 'Job Due Tomorrow',
            'CustomerJobDueWeekWasUpdated': 'Job Due Week Updated',
            'CustomerJobWasMarkedAsDone': 'Job marked as done',
            'CustomerJobWasMarkedAsDonePaid': deviceIsMd() ? 'Job marked as done (paid)' : 'Job done (paid)',
            'CustomerJobWasMarkedAsDoneOwing': deviceIsMd() ? 'Job marked as done (owing)' : 'Job done (owing)',
            'CustomerJobWasSkipped': 'Job Skipped',
            'CustomerJobWasScheduled': 'Job Scheduled',
            'InvoiceBecameOverdue': 'Invoice Overdue',
            'InvoiceBecameOverdue2nd': 'Invoice Overdue (2nd reminder)',
            'MessageSent': 'Message Sent',
            'Welcome': 'Welcome',
        },
        'ContactQuoteNotification': {
            'QuoteRequested': 'Quote Requested',
            'MessageSent': 'Message Sent',
            // 'MessageSeen': 'Message Seen',
            'QuoteWasEstimated': 'Quote Estimated',
            'QuoteWasItemised': 'Quote Itemised',
            // 'QuoteWasRejected': 'Quote Was Rejected',
        }
    };

    return statesToTitle[log.state.notification][log.state.state] ?? null;
}

const getEmailIcon = (log) => {
    if (log.emailResult?.sent !== true) {
        return ICON_NOT_SENT;
    }

    if (log.email?.status === null) {
        return ICON_SENT;
    }

    const emailStatuses = {
        'Bounce': ICON_FAILED,
        'Click': ICON_READ,
        // 'Complaint': ICON_FAILED,
        'Delivery': ICON_DELIVERED,
        'DeliveryDelay': ICON_SENT,
        'Open': ICON_READ,
        'Reject': ICON_FAILED,
        'RenderingFailure': ICON_FAILED,
        'Send': ICON_SENT,

        'Bounced': ICON_FAILED,
        'Clicked': ICON_READ,
        'Complaint': ICON_FAILED,
        'Delayed': ICON_SENT,
        'Delivered': ICON_DELIVERED,
        'Opened': ICON_READ,
        'Rejected': ICON_FAILED,
        'Sent': ICON_SENT,
    };

    return emailStatuses[log.email?.status] ?? ICON_UNKNOWN;
}

const getEmailStatus = (log) => {
    const emailStatuses = {
        'Bounce': 'Bounced',
        'Click': 'Clicked',
        // 'Complaint': 'Complaint',
        'Delivery': 'Delivered',
        'DeliveryDelay': 'Delayed',
        'Open': 'Opened',
        'Reject': 'Rejected',
        'RenderingFailure': 'Failed',
        'Send': 'Sent',

        // Internal names are already remapped

        'Bounced': 'Bounced',
        'Clicked': 'Clicked',
        'Complaint': 'Complaint',
        'Delayed': 'Delayed',
        'Delivered': 'Delivered',
        'Opened': 'Opened',
        'Rejected': 'Rejected',
        'Sent': 'Sent',
    };

    return emailStatuses[log.email?.status] ?? log.email?.status ?? <NoValue />;
}

const getSMSIcon = (log) => {
    if (log.smsResult?.sent !== true) {
        return ICON_NOT_SENT;
    }

    if (log.sms?.status === null) {
        return ICON_SENT;
    }

    const smsStatuses = {
        // Twilio
        'queued': ICON_SENT,
        'sending': ICON_SENT,
        'sent': ICON_SENT,
        'failed': ICON_FAILED,
        'delivered': ICON_DELIVERED,
        'undelivered': ICON_FAILED,
        'receiving': ICON_DELIVERED,
        'received': ICON_DELIVERED,
        'accepted': ICON_SENT,
        'scheduled': ICON_SENT,
        'read': ICON_READ,
        'partially_delivered': ICON_SENT,
        'cancelled': ICON_FAILED,
        // Telnyx
        // 'queued': ICON_SENT,
        'gw_timeout': ICON_FAILED,
        // 'sent': ICON_SENT,
        'dlr_timeout': ICON_FAILED,
        // 'delivered': ICON_DELIVERED,
        // 'failed': ICON_FAILED,

        'Sent': ICON_SENT,
        'Delivered': ICON_DELIVERED,
        'Failed': ICON_FAILED,
        'Sending': ICON_SENT,
        'Received': ICON_DELIVERED,
        'Read': ICON_READ,
    };

    return smsStatuses[log.sms?.status] ?? ICON_UNKNOWN;
}

const getSMSStatus = (log) => {
    const smsStatuses = {
        // Twilio
        'queued': 'Queued',
        'sending': 'Sending',
        'sent': 'Sent',
        'failed': 'Failed',
        'delivered': 'Delivered',
        'undelivered': 'Undelivered',
        'receiving': 'Receiving',
        'received': 'Received',
        'accepted': 'Accepted',
        'scheduled': 'Scheduled',
        'read': 'Read',
        'partially_delivered': 'Partially Delivered',
        'cancelled': 'Cancelled',
        // Telnyx
        // 'queued': 'Queued',
        'gw_timeout': 'Gateway Timeout',
        // 'sent': 'Sent',
        'dlr_timeout': 'Delivery Timeout',
        // 'delivered': 'Delivered',
        // 'failed': 'Failed',

        // Internal names are already remapped

        'Sent': 'Sent',
        'Delivered': 'Delivered',
        'Failed': 'Failed',
        'Sending': 'Sending',
        'Received': 'Received',
        'Read': 'Read',
    };

    return smsStatuses[log.sms?.status] ?? log.sms?.status ?? <NoValue />;
}

const Details = ({log, isOpen, setIsOpen, refreshThread}) => {
    return <Modal isOpen={isOpen} setIsOpen={setIsOpen} title={getNotificationLogTitle(log)+' notification'} dismissible>
        {(isOpen && !log.featureUnavailable && log.status === 'Deferred') &&  <Deferred {...{log, isOpen, setIsOpen, refreshThread}} />}
        {(isOpen && !log.featureUnavailable && log.status !== 'Deferred') &&  <Inner {...{log, isOpen, setIsOpen, refreshThread}} />}
        {(isOpen && log.featureUnavailable) && <p>
            This notification was not sent because you didn't have the <strong>{log.featureUnavailable}</strong> feature available at the time.
        </p>}
    </Modal>
}

const Deferred = ({log, setIsOpen, refreshThread}) => {
    return <div className="prose surface-1">
        <p>We're holding this notification for a minute in case you make further changes or the customer sees the message first.</p>

        <p>Depending on your notification settings, we'll send it shortly.</p>

        <ButtonRow>
            <Button theme="secondary"
                label="Close"
                onClick={() => setIsOpen(false)}
            />
            <Button theme="primary"
                label="Send now"
                onClick={() => {
                    return new Promise((resolve) => {
                        activateDeferrerNotifications(log.journalId)
                            .then(() => {
                                setTimeout(() => {
                                    refreshThread()
                                        .then(() => {
                                            setIsOpen(false);
                                            resolve();
                                        });
                                }, 1000);
                            });
                    });
                }}
                onClickLoading
            />
        </ButtonRow>
    </div>
}

const Inner = ({log, setIsOpen, refreshThread}) => {
    const newWindow = useNewWindow();
    const on = <Icon icon="Check" theme="solid" className={GREEN} />;
    const off = <Icon icon="XMark" theme="solid" className={RED} />;

    return <>
        {log.superseded && <Alert theme="info"
            icon="SendBackward"
            title="Superseded"
            message="This notification was superseded by a newer notification and will not be sent."
        />}
        {log.delayed && <Alert theme="info"
            icon="AlarmClock"
            title={<>Delayed until <DateTime date={log.notification?.delayedUntil} /></>}
            message={<>
                This notification has been delayed because it was sent outside of the 'allowed times'.<br />
                You can change this in customer notification settings.
            </>}
        />}
        {log.eventTriggerIgnored && <Alert theme="info"
            icon="Pause"
            title="Not sent"
            message="This notification was not sent immediately because your settings are configured to send it manually or at a scheduled time instead."
        />}
        {log.delayed && <ButtonRow>
            <Button theme="secondary"
                label="Close"
                onClick={() => setIsOpen(false)}
            />
            <Button theme="danger"
                label="Cancel notification"
                onClick={() => {
                    return new Promise((resolve) => {
                        cancelDelayedNotification(log.journalId, log.notificationId)
                            .then(() => {
                                setTimeout(() => {
                                    refreshThread()
                                        .then(() => {
                                            setIsOpen(false);
                                            resolve();
                                        });
                                }, 1000);
                            });
                    });
                }}
                onClickLoading
            />
        </ButtonRow>}
        {(!log.delayed && !log.superseded && !log.eventTriggerIgnored) && <>
            <p className="my-5">
                <DateTime date={log.loggedAt} />
            </p>
            <Sections>
                <Section title="Email">
                    <Grid items={[
                        [
                            <strong>Send attempted</strong>,
                            log.emailResult?.sent ? on : off
                        ],
                        [
                            <strong>Email address</strong>,
                            log.emailResult?.hadRecipient ? on : off
                        ],
                        [
                            <strong>Notification enabled</strong>,
                            log.emailResult?.notificationEnabled ? on : off
                        ],
                        [
                            <strong>Triggered</strong>,
                            // Null counts as true
                            log.emailResult?.eventTrigger === false ? off : on
                        ],
                        // [
                        //     <strong>Email enabled</strong>,
                        //     log.emailResult?.notificationUsesChannel ? on : off
                        // ],
                        // [
                        //     <strong>Customer preference</strong>,
                        //     log.emailResult?.hadRecipient ? on : off
                        // ],
                        [
                            <strong>Delivery Status</strong>,
                            getEmailStatus(log)
                        ],
                    ]} />
                </Section>
                <Section title="SMS">
                    <Grid items={[
                        [
                            <strong>Send attempted</strong>,
                            log.smsResult?.sent ? on : off
                        ],
                        [
                            <strong>Phone Number</strong>,
                            log.smsResult?.hadRecipient ? on : off
                        ],
                        [
                            <strong>Notification enabled</strong>,
                            log.smsResult?.notificationEnabled ? on : off
                        ],
                        [
                            <strong>Triggered</strong>,
                            // Null counts as true
                            log.smsResult?.eventTrigger === false ? off : on
                        ],
                        // [
                        //     <strong>SMS enabled</strong>,
                        //     log.smsResult?.notificationUsesChannel ? on : off
                        // ],
                        // [
                        //     <strong>Customer preference</strong>,
                        //     log.smsResult?.hadRecipient ? on : off
                        // ],
                        [
                            <strong>Delivery Status</strong>,
                            getSMSStatus(log)
                        ],
                    ]} />
                </Section>
            </Sections>
        </>}
        {(!log.delayed) && <ButtonRow arrange="between">
            <Button theme="light"
                label="More info"
                icon="LinkExternal"
                onClick={() => newWindow('https://getsoapy.com/docs/customer-notification-logs')}
            />
            <Button theme="secondary"
                label="Close"
                onClick={() => setIsOpen(false)}
            />
        </ButtonRow>}
    </>
}

export default NotificationLog;
